import { useState, type FormEvent } from "react";
import { AnimatePresence, motion } from "motion/react";
import { canBeBillable, entryInvoice, isLive, overlappingEntries, rateSnapshotFor } from "@otrack/domain";
import { Button, DateField, Field, FieldError, Icon, Modal, Select, Switch, TextInput, TimeField } from "@otrack/ui";
import { colorOf } from "./calendar/model";
import { taskOptions } from "./calendar/popover";
import { projectRefs } from "./commands";
import { useApp, useDataset, useFactory } from "./context";
import { href } from "./router";
import { formatDuration, fromTimeInput, keepExact, startOfDay, toTimeInput } from "./time";

export function EntryEditor({ entryId, onClose }: { entryId: string; onClose: () => void }) {
  const { act } = useApp();
  const dataset = useDataset();
  const factory = useFactory();
  const entry = dataset.timeEntries[entryId];
  const [projectId, setProjectId] = useState(entry?.projectId ?? "");
  const [taskId, setTaskId] = useState(entry?.taskId ?? "");
  const [date, setDate] = useState<number | null>(entry ? startOfDay(entry.start) : null);
  const [description, setDescription] = useState(entry?.description ?? "");
  const [start, setStart] = useState(entry ? toTimeInput(entry.start) : "");
  const [end, setEnd] = useState(entry?.end ? toTimeInput(entry.end) : "");
  const [billable, setBillable] = useState(entry?.billable ?? false);
  const [error, setError] = useState<string | null>(null);

  if (!entry || !isLive(entry)) return null;
  const invoice = entryInvoice(dataset, entry);
  // Time on a Sent or Paid Invoice is locked; a Draft only reserves it.
  const locked = invoice !== undefined && invoice.status !== "draft";
  const project = projectId ? dataset.projects[projectId] : undefined;
  const billableAllowed = canBeBillable(project);
  const projects = projectRefs(dataset);
  const tasks = projectId ? taskOptions(dataset, projectId, entry.taskId) : [];
  if (entry.projectId && !projects.some((ref) => ref.id === entry.projectId)) {
    const current = dataset.projects[entry.projectId];
    if (current) projects.push({ id: current.id, name: current.name, clientName: null });
  }

  const save = async (event: FormEvent) => {
    event.preventDefault();
    if (locked) return;
    const day = date;
    if (day === null) return setError("Pick a day.");
    const nextStart = keepExact(fromTimeInput(day, start), entry.start);
    let nextEnd = end ? keepExact(fromTimeInput(day, end), entry.end) : null;
    // An end earlier than the start means the entry ran past midnight.
    if (nextStart !== null && nextEnd !== null && nextEnd <= nextStart) nextEnd = keepExact(fromTimeInput(startOfDay(day, 1), end), entry.end);
    if (nextStart === null) return setError("Enter a start time.");
    if (end && nextEnd === null) return setError("Enter a valid end time.");
    if (nextEnd !== null && nextEnd <= nextStart) return setError("The end must be after the start.");
    if (nextEnd === null && entry.end !== null) return setError("Enter an end time.");
    const now = Date.now();
    if (nextStart > now || (nextEnd !== null && nextEnd > now)) return setError("Time can only be logged up to now.");
    const overlaps = overlappingEntries(dataset, nextStart, nextEnd ?? now, now).filter((other) => other.id !== entry.id);
    if (overlaps.length) return setError("This overlaps another entry.");

    const nextProjectId = projectId || null;
    const nextBillable = billableAllowed && billable;
    const patch: Record<string, unknown> = {};
    if (description !== entry.description) patch["description"] = description;
    const nextTaskId = nextProjectId && taskId && dataset.tasks[taskId]?.projectId === nextProjectId ? taskId : null;
    if (nextTaskId !== entry.taskId) patch["taskId"] = nextTaskId;
    if (nextStart !== entry.start) patch["start"] = nextStart;
    if (nextEnd !== entry.end) patch["end"] = nextEnd;
    if (nextProjectId !== entry.projectId || nextBillable !== entry.billable) {
      patch["projectId"] = nextProjectId;
      patch["billable"] = nextBillable;
      patch["rateSnapshot"] = rateSnapshotFor(dataset, nextProjectId, nextBillable);
    }
    if (Object.keys(patch).length) await act([factory.op("timeEntry", entry.id, patch)], "Entry updated");
    onClose();
  };

  const remove = async () => {
    if (locked) return;
    const now = Date.now();
    // Leaving a Draft too, so restoring the entry later finds it uninvoiced.
    await act([factory.op("timeEntry", entry.id, { deletedAt: now, ...(entry.end === null ? { end: Math.max(entry.start, now) } : {}), ...(invoice ? { invoiceId: null } : {}) })], "Moved to Trash");
    onClose();
  };

  // A live preview of what the times add up to, including runs past midnight.
  const previewStart = date === null ? null : fromTimeInput(date, start);
  let previewEnd = date === null || !end ? null : fromTimeInput(date, end);
  if (previewStart !== null && previewEnd !== null && previewEnd <= previewStart && date !== null) previewEnd = fromTimeInput(startOfDay(date, 1), end);
  const running = entry.end === null;
  const spanEnd = previewEnd ?? (running ? Date.now() : null);
  const span = previewStart === null || spanEnd === null ? null : spanEnd - previewStart;
  const overnight = previewStart !== null && previewEnd !== null && date !== null && previewEnd >= startOfDay(date, 1);

  return <Modal title={running ? "Running timer" : "Time entry"} icon="clock" onClose={onClose}>
    <form className="ot-form" onSubmit={(event) => void save(event)} noValidate>
      {invoice && <p className="ot-note"><Icon name={locked ? "warning" : "receipt"}/><span>
        {locked ? <>This entry is on <a href={href({ page: "invoice", id: invoice.id })} onClick={onClose}>{invoice.number ?? "a sent invoice"}</a>, so it is locked. Move that invoice back to draft to change it.</>
          : <>On a <a href={href({ page: "invoice", id: invoice.id })} onClick={onClose}>draft invoice</a>. Changes show up on it right away.</>}
      </span></p>}
      <fieldset className="ot-fieldset" disabled={locked}>
      <Field label="Project">
        <Select
          value={projectId}
          searchable={projects.length > 7}
          searchPlaceholder="Find a project"
          onChange={(next) => { setProjectId(next); setTaskId(""); }}
          options={[{ value: "", label: "No project", swatch: colorOf(dataset, null) }, ...projects.map((ref) => ({ value: ref.id, label: ref.name, hint: ref.clientName ?? undefined, swatch: colorOf(dataset, ref.id) }))]}
        />
      </Field>
      <AnimatePresence initial={false}>
        {tasks.length > 0 && <motion.div key={`tasks:${projectId}`} initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: "auto" }} exit={{ opacity: 0, height: 0 }} transition={{ duration: 0.28, ease: [0.16, 1, 0.3, 1] }} style={{ overflow: "hidden" }}>
          <Field label="Task" optional>
            <Select value={taskId} onChange={setTaskId} options={tasks}/>
          </Field>
        </motion.div>}
      </AnimatePresence>
      <Field label="Description">
        <TextInput value={description} onChange={(event) => setDescription(event.target.value)} maxLength={1000} placeholder="What did you work on?"/>
      </Field>
      <Field label="Day">
        <DateField value={date} onChange={setDate} max={Date.now()}/>
      </Field>
      <div className="ot-times">
        <Field label="Start"><TimeField value={start} onChange={setStart}/></Field>
        <span className="ot-times__arrow" aria-hidden="true">→</span>
        <Field label="End"><TimeField value={end} onChange={setEnd} placeholder={running ? "Running" : "17:00"}/></Field>
        <AnimatePresence mode="popLayout" initial={false}>
          <motion.span key={span === null ? "none" : formatDuration(span)} className={`ot-times__total ${running && !end ? "is-live" : ""}`} initial={{ opacity: 0, y: 4 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -4 }} transition={{ duration: 0.18 }}>
            {span === null || span <= 0 ? "—" : formatDuration(span)}{overnight ? <small>+1 day</small> : null}
          </motion.span>
        </AnimatePresence>
      </div>
      <div className="ot-form__card">
        <Switch
          label="Billable"
          description={billableAllowed ? "Counts toward earnings at the project's rate." : "This project is non-billable."}
          checked={billableAllowed && billable}
          disabled={!billableAllowed}
          onChange={setBillable}
        />
      </div>
      </fieldset>
      <FieldError>{error}</FieldError>
      <div className="ot-form__actions">
        {!locked && <Button type="button" tone="ghost" icon="trash" className="ot-button--quiet-danger" onClick={() => void remove()}>Move to Trash</Button>}
        <span className="ot-form__spacer"/>
        <Button type="button" onClick={onClose}>{locked ? "Close" : "Cancel"}</Button>
        {!locked && <Button type="submit" tone="primary">Save</Button>}
      </div>
    </form>
  </Modal>;
}
